import React, { useState, useEffect } from 'react';
import { getThingAnomalies } from '../../../../services';
import { formatValue } from '../../../../utils';
import * as s from './styled-machine-anomalies';

// Desvios do padrao (anomalias v1): compara o valor ATUAL de cada variavel
// da maquina selecionada com o baseline robusto dela (mediana/MAD dos
// ultimos 14 dias, so amostras rodando). |z|>=2.5 = atencao, >=4 = critico.
// E indicador estatistico de "fora do normal DESTA maquina" — antecede o
// alarme de range, mas nao o substitui.

const POLL_MS = 60 * 1000;

const fmtVal = (v, unit) => {
  if (v == null || !Number.isFinite(Number(v))) return '—';
  return `${formatValue(v)}${unit ? ` ${unit}` : ''}`;
};

const MachineAnomalies = ({ thing }) => {
  const thingId = thing && thing.id;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (thingId == null) return undefined;
    let active = true;
    setLoading(true);
    setError(false);
    setData(null);
    const fetchIt = () => {
      getThingAnomalies(thingId)
        .then((res) => {
          if (!active) return;
          let body = (res && res.data) || res || null;
          if (body && body.data && body.data.anomalies !== undefined) {
            body = body.data;
          }
          setData(body && body.anomalies !== undefined ? body : null);
          setError(!(body && body.anomalies !== undefined));
          setLoading(false);
        })
        .catch(() => {
          if (!active) return;
          setError(true);
          setLoading(false);
        });
    };
    fetchIt();
    const id = setInterval(fetchIt, POLL_MS);
    return () => {
      active = false;
      clearInterval(id);
    };
  }, [thingId]);

  if (thingId == null) return null;

  const anomalies = (data && data.anomalies) || [];

  return (
    <s.Wrap>
      {loading ? (
        <s.Empty>Avaliando desvios…</s.Empty>
      ) : error ? (
        <s.Empty>Não foi possível avaliar os desvios desta máquina.</s.Empty>
      ) : data && data.running === false ? (
        <s.Empty>
          Máquina parada — a avaliação de desvios fica pausada (valores em
          repouso não representam operação).
        </s.Empty>
      ) : anomalies.length === 0 ? (
        <s.Ok>
          {/* lucide:check-circle */}
          <svg
            width="15"
            height="15"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
            <path d="M22 4 12 14.01l-3-3" />
          </svg>
          Operando dentro do padrão dos últimos{' '}
          {(data && data.baselineDays) || 14} dias
          {data && data.checkedCount
            ? ` (${data.checkedCount} variáveis avaliadas)`
            : ''}
          .
        </s.Ok>
      ) : (
        <s.List>
          {anomalies.map((a) => (
            <s.Row key={a.variable} $severity={a.severity}>
              <s.RowHead>
                <s.VarName title={a.variable}>
                  {a.name || a.variable}
                </s.VarName>
                <s.ZBadge $severity={a.severity} title="Desvio em relação ao padrão da máquina (z-score robusto)">
                  {a.direction === 'low' ? '▾' : '▴'} {Math.abs(Number(a.zScore) || 0).toFixed(1)}σ
                </s.ZBadge>
              </s.RowHead>
              <s.RowVals>
                <s.Current $severity={a.severity}>
                  {fmtVal(a.current, a.unit)}
                </s.Current>
                <s.Typical>
                  típico {fmtVal(a.typicalP10, '')}–{fmtVal(a.typicalP90, a.unit)}
                </s.Typical>
              </s.RowVals>
            </s.Row>
          ))}
        </s.List>
      )}
    </s.Wrap>
  );
};

export default MachineAnomalies;
